import {
  ArrowRightOutlined,
  CloseOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { EspDomainType } from '@mm/state/model/EspModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { Spin, Steps } from 'antd';
import classNames from 'classnames';
import React, { useState } from 'react';
import CheckBox from '../../components/Checkbox';
import PrimarySelect, {
  DropdownOption,
} from '../../components/PrimarySelect/PrimarySelect';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import finishIcon from 'assets/clapping.png';
import styles from './ValidateDNSModal.module.css';
import { useEspCRUDActions } from '@mm/state/actions/EspCRUDActions';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';

const dnsProviders = [
  { id: 'google_cloud', name: 'Google Cloud DNS' },
  { id: 'cloudflare', name: 'Cloudflare' },
  { id: 'quad9', name: 'Quad9' },
  { id: 'openDNS', name: 'OpenDNS' },
  { id: 'amazon', name: 'Amazon Route 53' },
  { id: 'cloudDNS', name: 'CloudDNS' },
  { id: 'goDaddy', name: 'GoDaddy' },
  { id: 'other', name: 'Other' },
] as DropdownOption[];

export const ValidateDNSModal = ({ domain }: { domain: EspDomainType }) => {
  const { closeModal } = useModalActions();
  const { validateDomain } = useEspCRUDActions();
  const busy = useSelector((state: StoreState) => state.esp.busy);

  const [current, setCurrent] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState(dnsProviders[0]);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const removeDomainFromDKIM = (dkim, domain) => {
    const suffix = `.${domain}`;
    if (dkim.endsWith(suffix)) {
      return dkim.slice(0, -suffix.length);
    }
    return dkim;
  };

  const next = () => {
    setCurrent(current + 1);
    setCheckboxChecked(false);
  };

  const onDone = async () => {
    await validateDomain(domain.id);
    closeModal();
  };

  const dnsProviderSelector = (
    <div className={styles.dnsSelectorContainer}>
      <div style={{ fontWeight: 500 }}>Who is your DNS Server Provider?</div>
      <PrimarySelect
        value={selectedProvider}
        options={dnsProviders}
        onChange={setSelectedProvider}
      />
    </div>
  );

  const signInDNSInstructions = (
    <ol>
      <li className={styles.text}>
        <span className={styles.textBold}>
          Open a new tab or window and navigate to your domain provider's
          website
        </span>{' '}
        to access your domain records.
      </li>
      <li className={styles.text}>
        Find the page or area that has your domain or domain records. Things to
        search for are{' '}
        <span className={styles.textBold}>
          manage domains, DNS, custom records, or zone files.
        </span>
      </li>
      <li className={styles.text}>
        Your domains are usually in a list form. If you can't find the right
        page, try searching for your domain provider's help center for{' '}
        <span className={styles.textBold}>DNS</span> or{' '}
        <span className={styles.textBold}>CNAME.</span>
      </li>
    </ol>
  );

  const createDKIMRecordContent = (
    <div className={styles.dkimIntructionsContainer}>
      <div className={styles.text}>
        You need to add two CNAME records to your domain via your domain
        provider's website or interface. These records will help us verify your
        email:
      </div>
      <ol className={styles.dkimIntructionsContainer}>
        <li className={styles.text}>
          Create a <span className={styles.textBold}>new record</span> and set
          the Type to <span className={styles.textBold}>CNAME.</span>
        </li>
        <li className={styles.text}>
          Copy the{' '}
          <span className={styles.textBold}>Name and Value from CNAME 1</span>{' '}
          below and paste them into the corresponding Name (or Host / Hostname /
          Host) and Value (or Data / Points To) fields for your record:
        </li>
        <div className={styles.box}>
          <span className={styles.textBold}>Name:</span>{' '}
          {removeDomainFromDKIM(domain.dns.dkim1.host, domain.domain)}
          <CopyToClipboard
            text={removeDomainFromDKIM(domain.dns.dkim1.host, domain.domain)}
          >
            <Button
              leftIcon={<CopyOutlined />}
              color="transparent-no-shadow"
              css={{ container: styles.copyIcon }}
            >
              Copy
            </Button>
          </CopyToClipboard>
        </div>
        <div className={styles.box}>
          <span className={styles.textBold}>Value:</span>{' '}
          {domain.dns.dkim1.data}
          <CopyToClipboard text={domain.dns.dkim1.data}>
            <Button
              leftIcon={<CopyOutlined />}
              color="transparent-no-shadow"
              css={{ container: styles.copyIcon }}
            >
              Copy
            </Button>
          </CopyToClipboard>
        </div>
        <li className={styles.text}>
          Leave <span className={styles.textBold}>TTL as the default</span> or
          choose the lowest option.
        </li>
        <li className={styles.text}>
          <span className={styles.textBold}>Save</span> the record.
        </li>
        <li className={styles.text}>
          Repeat the same steps for{' '}
          <span className={styles.textBold}>CNAME 2:</span>
        </li>
        <div className={styles.box}>
          <span className={styles.textBold}>Name:</span>{' '}
          {removeDomainFromDKIM(domain.dns.dkim2.host, domain.domain)}
          <CopyToClipboard
            text={removeDomainFromDKIM(domain.dns.dkim2.host, domain.domain)}
          >
            <Button
              leftIcon={<CopyOutlined />}
              color="transparent-no-shadow"
              css={{ container: styles.copyIcon }}
            >
              Copy
            </Button>
          </CopyToClipboard>
        </div>
        <div className={styles.box}>
          <span className={styles.textBold}>Value:</span>{' '}
          {domain.dns.dkim2.data}
          <CopyToClipboard text={domain.dns.dkim2.data}>
            <Button
              leftIcon={<CopyOutlined />}
              color="transparent-no-shadow"
              css={{ container: styles.copyIcon }}
            >
              Copy
            </Button>
          </CopyToClipboard>
        </div>
      </ol>
    </div>
  );

  const createDMARCRecordContent = (
    <div className={styles.dkimIntructionsContainer}>
      <div className={styles.text}>
        Add a TXT record in your DNS provider to help ensure proper email
        delivery:
      </div>
      <ol className={styles.dkimIntructionsContainer}>
        <li className={styles.text}>
          Create a <span className={styles.textBold}>new DNS record</span> and
          set the Type to <span className={styles.textBold}>CNAME.</span>
        </li>
        <li className={styles.text}>Copy the following values:</li>
        <div className={styles.box}>
          <span className={styles.textBold}>Name:</span>{' '}
          {removeDomainFromDKIM(domain.dns.mailCname.host, domain.domain)}
          <CopyToClipboard
            text={removeDomainFromDKIM(
              domain.dns.mailCname.host,
              domain.domain,
            )}
          >
            <Button
              leftIcon={<CopyOutlined />}
              color="transparent-no-shadow"
              css={{ container: styles.copyIcon }}
            >
              Copy
            </Button>
          </CopyToClipboard>
        </div>
        <div className={styles.box}>
          <span className={styles.textBold}>Value:</span>{' '}
          {domain.dns.mailCname.data}
          <CopyToClipboard text={domain.dns.mailCname.data}>
            <Button
              leftIcon={<CopyOutlined />}
              color="transparent-no-shadow"
              css={{ container: styles.copyIcon }}
            >
              Copy
            </Button>
          </CopyToClipboard>
        </div>
        <li className={styles.text}>
          Leave <span className={styles.textBold}>TTL as the default</span> or
          choose the lowest option.
        </li>
        <li className={styles.text}>
          <span className={styles.textBold}>Save</span> the record.
        </li>
      </ol>
    </div>
  );

  const finishValidationContent = (
    <div className={classNames(styles.text, styles.textCentered)}>
      The process may take up to 48 hours, but it's usually quicker. We will
      notify you via email once validation is complete or provide instructions
      if there are any errors.
    </div>
  );

  const steps = [
    {
      title: 'Choose DNS Provider',
      content: dnsProviderSelector,
      contentTitle: 'Choose your DNS provider',
      subtitle: 'This allows us to gather leads from your traffic',
    },
    {
      title: 'Sign in DNS',
      content: signInDNSInstructions,
      contentTitle: "Sign in DNS to provider's account",
      checkboxTitle:
        'By marking this checkbox, I confirm that I am in my DNS provider account now',
    },
    {
      title: 'Create a DKIM record',
      content: createDKIMRecordContent,
      contentTitle: 'Create a DKIM record',
      checkboxTitle:
        "By marking this checkbox, I confirm that I've created a DKIM record",
    },
    {
      title: 'Create a DMARC record',
      content: createDMARCRecordContent,
      contentTitle: 'Create a DMARC record',
      checkboxTitle:
        "By marking this checkbox, I confirm that I've created a DMARC record",
    },
    {
      title: 'Finish Validation',
      content: finishValidationContent,
      contentTitle: 'Wait for Customers.ai to validate the records',
    },
  ];

  const items = steps.map(item => ({ key: item.title, title: item.title }));

  const nextDisabled = steps[current].checkboxTitle ? !checkboxChecked : false;
  const isLastStep = current === steps.length - 1;

  return (
    <Spin spinning={busy}>
      <div className={styles.container}>
        <CloseOutlined
          className={styles.closeIcon}
          onClick={() => closeModal()}
        />
        <Steps
          className={styles.steps}
          progressDot
          current={current}
          items={items}
        />
        <div className={styles.body}>
          {isLastStep && (
            <img
              src={finishIcon}
              alt="Clapping"
              className={classNames(styles.icon, styles.centered)}
            />
          )}
          <div className={styles.header}>
            {isLastStep && (
              <div className={styles.tag}>You did a great job!</div>
            )}
            <div className={styles.title}>{steps[current].contentTitle}</div>
            {steps[current].subtitle && (
              <div className={styles.subtitle}>{steps[current].subtitle}</div>
            )}
          </div>
          <div>{steps[current].content}</div>
        </div>
        <div className={styles.footer}>
          {steps[current].checkboxTitle && (
            <div className={styles.box}>
              <CheckBox
                className={styles.checkbox}
                isChecked={checkboxChecked}
                onClick={() => setCheckboxChecked(!checkboxChecked)}
                label={steps[current].checkboxTitle}
              />
            </div>
          )}
          <div
            className={classNames(styles.buttons, {
              [styles.centered]: !steps[current].checkboxTitle,
            })}
          >
            {current < steps.length - 1 && (
              <Button
                disabled={nextDisabled}
                css={{ container: styles.button }}
                onClick={() => next()}
              >
                <ArrowRightOutlined /> Next
              </Button>
            )}
            {isLastStep && (
              <Button css={{ container: styles.button }} onClick={onDone}>
                Got it
              </Button>
            )}
          </div>
        </div>
      </div>
    </Spin>
  );
};
